/*
 * File: input-date.ts
 * File Created: 2024-09-13 09:29:09
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2024-09-24 06:56:34
 * Modified By: Val Liu
 * -----
 */

import { Component, Prop } from "vue-property-decorator";
import { BaseInputComponent } from "../core/lib/base-input-component";
import { PropInnateSync } from "@root/core/vue-props";
import { DateHelper } from "@root/core";

export interface IInputDateData {
    "date-format"?: string;
}
const defaultData: IInputDateData = {
    "date-format": "Y/m/d",
};

@Component
export default class InputDate extends BaseInputComponent {
    @PropInnateSync("value", { type: [Date, Array, null], required: false })
    innateValue!: Date | Date[] | null;

    @Prop({ type: Object, required: false })
    declare data: IInputDateData;
    get mergedData() {
        return {
            ...defaultData,
            ...this.data,
        };
    }
    /// private helper
    // simple version
    get generatedInnateValue() {
        /// allow null
        if (this.innateValue == null) return this.innateValue;

        let newValue: any = this.innateValue;
        /// convert the value if only time.
        const timeOnly = (this.$attrs.config as any)?.noCalendar == true;
        newValue = transformDateToString(newValue, timeOnly);
        if (newValue == null) return newValue;

        if (this.multiple === true || this.multiple === "range") {
            if (newValue.length === 0) return null;
            if (newValue.length === 1) return newValue[0];
        }
        /// normal case
        return newValue;
    }
    // receiving `string` and emit the `Date` value.
    updateDateValue(date: Date, dateStr: string) {
        const newValue = transformStringToDate(dateStr);

        /// if equal with previous value, don't emit.
        if (
            newValue === this.innateValue ||
            JSON.stringify(newValue) === JSON.stringify(this.innateValue)
        )
            return;

        /// emit the value
        this.innateValue = newValue;
    }
}

/// detect ex: 10:00 PM
function isDateStringTimeOnly(value: string) {
    return /^(1[0-2]|0?[1-9]):[0-5][0-9] [AP]M$/.test(value);
}

function transformDateToString(
    value: Date | Date[] | null,
    timeOnly: boolean = false,
): string | string[] | null {
    if (value == null) return null;
    if (Array.isArray(value)) {
        return value.map((o) => transformDateToString(o)).filter((o) => !!o) as any;
    }
    /// detect Invalid Date
    if (isNaN(value.getTime())) return null;

    const result = timeOnly
        ? DateHelper.toString(
              value,
              {
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
              },
              "en-US",
          )
        : value.toISOString();

    return result;
}

function transformStringToDate(value: string | string[] | null): Date | Date[] | null {
    if (value == null) return null;

    {
        /// make comma seperated string into array
        const regex = /\,/;
        if (!Array.isArray(value) && /\,/.test(value)) {
            value = value.split(regex).map((o) => o.trim());
        }
    }

    {
        /// make range wierd seprated string into array
        const regex = /\s(?!\b(?:AM|PM)\b)(?:[\w\p{L}]+)\s/u;
        if (!Array.isArray(value) && regex.test(value)) {
            value = value.split(regex).map((o) => o.trim());
        }
    }

    /// do transform
    if (Array.isArray(value)) {
        return value.map((o) => transformStringToDate(o)).filter((o) => !!o) as any;
    }
    /// iv-input-time case.
    let newDate = new Date(isDateStringTimeOnly(value) ? `1970/01/01 ${value}` : value);
    return newDate;
}
